import React, { useState, useEffect } from 'react';
import { api } from './lib/api';
import Login from './pages/Login';
import Chat from './pages/Chat';
import Settings from './pages/Settings';

type Page = 'login' | 'chat' | 'bf-settings';

export default function App() {
  const [page, setPage] = useState<Page>('login');
  const [loading, setLoading] = useState(true);

  const navigateTo = (p: Page) => {
    setPage(p);
    const path = p === 'login' ? '/' : `/${p}`;
    window.history.pushState({}, '', path);
  };

  useEffect(() => {
    const handleRoute = () => {
      const path = window.location.pathname;
      if (path === '/chat') setPage('chat');
      else if (path === '/bf-settings') setPage('bf-settings');
      else if (path === '/' || path === '/login') setPage(api.isAuthenticated() ? 'chat' : 'login');
      else setPage(api.isAuthenticated() ? 'chat' : 'login');
    };

    // Handle magic link verification
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get('token');
    if (window.location.pathname === '/verify' && verifyToken) {
      api.verifyToken(verifyToken).then((result) => {
        if (result.success) {
          window.history.replaceState({}, '', '/chat');
          setPage('chat');
        } else {
          window.history.replaceState({}, '', '/');
          setPage('login');
        }
      }).catch(() => {
        window.history.replaceState({}, '', '/');
        setPage('login');
      }).finally(() => setLoading(false));
      return;
    }

    // Check auth state
    if (api.isAuthenticated()) {
      api.getMe().then(() => {
        handleRoute();
        if (page === 'login') setPage('chat');
      }).catch(() => {
        api.clearSession();
        setPage('login');
        window.history.replaceState({}, '', '/');
      }).finally(() => setLoading(false));
    } else {
      const isPublic = ['/', '/login', '/verify'].includes(window.location.pathname);
      if (!isPublic) {
        window.history.replaceState({}, '', '/');
      }
      setPage('login');
      setLoading(false);
    }

    window.addEventListener('popstate', handleRoute);

    // Listen for custom navigate events (from Chat page header etc.)
    const onNav = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.page) navigateTo(detail.page as Page);
    };
    window.addEventListener('navigate', onNav);

    return () => {
      window.removeEventListener('popstate', handleRoute);
      window.removeEventListener('navigate', onNav);
    };
  }, []);

  if (loading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#09090b', gap: 12,
      }}>
        <div style={{
          width: 32, height: 32,
          border: '2px solid #27272a', borderTopColor: '#a78bfa',
          borderRadius: '50%', animation: 'spin 0.7s linear infinite',
        }} />
        <span style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>加载中...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  switch (page) {
    case 'login':
      return <Login onLoginSuccess={() => navigateTo('chat')} />;
    case 'chat':
      return <Chat />;
    case 'bf-settings':
      return <Settings onBack={() => navigateTo('chat')} />;
    default:
      return <Login onLoginSuccess={() => navigateTo('chat')} />;
  }
}
